import { useQuery } from '@tanstack/react-query'
import apiInstance from '@services/api/apiInstance'
import {
  Country,
  CountriesApiResponse,
  Sector,
  SectorsApiResponse,
  EmploymentForm,
  EmploymentFormsApiResponse
} from './meta.models'
import { FALLBACK_COUNTRIES, FALLBACK_SECTORS } from './meta.fallback'

const META_QUERY_KEY = 'meta'

export const useCountries = () => {
  return useQuery({
    queryKey: [META_QUERY_KEY, 'countries'],
    queryFn: async (): Promise<Country[]> => {
      try {
        const response = await apiInstance.get<CountriesApiResponse>('/meta/countries/')
        // Check if response has results field or is direct array
        if (Array.isArray(response.data)) {
          return response.data as Country[]
        }

        return response.data.results || []
      } catch (error) {
        // Fallback to hardcoded countries when server is unavailable
        console.warn('Failed to fetch countries from server, using fallback data:', error)
        return FALLBACK_COUNTRIES
      }
    },
    enabled: true,
    // Countries are relatively static data, cache for longer
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 24 * 60 * 60 * 1000 // 24 hours
  })
}

export const useSectors = () => {
  return useQuery({
    queryKey: [META_QUERY_KEY, 'sectors'],
    queryFn: async (): Promise<Sector[]> => {
      try {
        const response = await apiInstance.get<SectorsApiResponse>('/meta/sectors/')
        // Check if response has results field or is direct array
        if (Array.isArray(response.data)) {
          return response.data as Sector[]
        }

        return response.data.results || []
      } catch (error) {
        // Fallback to hardcoded sectors when server is unavailable
        console.warn('Failed to fetch sectors from server, using fallback data:', error)
        return FALLBACK_SECTORS
      }
    },
    enabled: true,
    // Sectors are relatively static data, cache for longer
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 24 * 60 * 60 * 1000 // 24 hours
  })
}

export const useEmploymentForms = () => {
  return useQuery({
    queryKey: [META_QUERY_KEY, 'employment-forms'],
    queryFn: async (): Promise<EmploymentForm[]> => {
      try {
        const response =
          await apiInstance.get<EmploymentFormsApiResponse>('/meta/employment-forms/')
        // Check if response has results field or is direct array
        if (Array.isArray(response.data)) {
          return response.data as EmploymentForm[]
        }

        return response.data.results || []
      } catch (error) {
        console.error('Failed to fetch employment forms from server:', error)
        return []
      }
    },
    enabled: true,
    // Employment forms are relatively static data, cache for longer
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 24 * 60 * 60 * 1000 // 24 hours
  })
}
